import React, { useState, useEffect } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../../firebase/config";
import { useCrewStore } from "../../store/crewStore";
import { useProfileStore } from "../../store/profileStore";
import { getDateInfo, formatTime } from "../../utils";
import CrewCard from "./CrewCard";
import AddCrewModal from "./modals/AddCrewModal";
import DownloadRoutesBtn from "./DownloadRoutesBtn";
import RouteBuilder from "./RouteBuilder";
import DashboardTab from "./DashboardTab";
import ProfileSection from "./ProfileSection";
import DayOffRequestTab from "./Dayoffrequesttab";
import type { FilterType } from "../../types";

type HomeTab = "builder" | "profiles" | "notes" | "dashboard" | "dayoff";

const HomeView: React.FC = () => {
  const {
    crews,
    viewingDate,
    searchQuery,
    activeFilter,
    setViewingDate,
    setSearchQuery,
    setActiveFilter,
    setCurrentCrewId,
    updateCrew,
  } = useCrewStore();
  const profiles = useProfileStore((s) => s.profiles);

  const [showAddCrew, setShowAddCrew] = useState(false);
  const [activeTab, setActiveTab] = useState<HomeTab>("builder");
  const [dayoffPending, setDayoffPending] = useState(0);
  useEffect(() => {
    const rRef = ref(db, "dayoff_requests");
    const unsub = onValue(rRef, (snap) => {
      const raw = snap.val();
      if (!raw) {
        setDayoffPending(0);
        return;
      }
      setDayoffPending(
        (Object.values(raw) as any[]).filter((r) => r.status === "pending")
          .length,
      );
    });
    return () => unsub();
  }, []);

  const di = getDateInfo(viewingDate);
  const dateCrews = crews.filter((c) => c.workDate === di.shortStr);
  const q = searchQuery.toLowerCase();

  let filtered = q
    ? dateCrews.filter(
        (c) =>
          c.driver.name.toLowerCase().includes(q) ||
          c.sideman.name.toLowerCase().includes(q),
      )
    : dateCrews;
  if (activeFilter === "pending")
    filtered = filtered.filter(
      (c) => !c.driver.confirmed || !c.sideman.confirmed,
    );
  if (activeFilter === "ready")
    filtered = filtered.filter(
      (c) => c.driver.confirmed && c.sideman.confirmed,
    );

  const totalForDate = dateCrews.length;
  const readyCount = dateCrews.filter(
    (c) => c.driver.confirmed && c.sideman.confirmed,
  ).length;

  // Collect all notes from all crews across all dates
  const allNotes: {
    crewId: string;
    name: string;
    role: string;
    routeNumber: string;
    workDate: string;
    note: string;
    confirmedAt: string;
    phone: string | null;
  }[] = [];
  crews.forEach((c) => {
    (["driver", "sideman"] as const).forEach((role) => {
      const m = c[role];
      if (m.confirmed && m.acceptNote && m.acceptNote.trim()) {
        const phone =
          profiles.find(
            (p) =>
              `${p.firstName}${p.lastName ? " " + p.lastName : ""}`.toLowerCase() ===
              m.name.toLowerCase(),
          )?.phone || null;
        allNotes.push({
          crewId: c.id,
          name: m.name,
          role,
          routeNumber: c.routeNumber || "Route",
          workDate: c.workDate,
          note: m.acceptNote,
          confirmedAt: m.confirmedAt || "",
          phone,
        });
      }
    });
  });
  allNotes.sort(
    (a, b) =>
      new Date(b.confirmedAt).getTime() - new Date(a.confirmedAt).getTime(),
  );

  // Delete a note — clears acceptNote from the crew member record in Firebase
  const deleteNote = (crewId: string, role: "driver" | "sideman") => {
    if (!confirm("Delete this note? This cannot be undone.")) return;
    updateCrew(crewId, (c) => ({
      ...c,
      [role]: { ...c[role], acceptNote: null },
    }));
  };

  const prevDay = () => {
    const d = viewingDate;
    setViewingDate(new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1));
  };
  const nextDay = () => {
    const d = viewingDate;
    setViewingDate(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1));
  };

  return (
    <>
      {/* HEADER */}
      <div className="app-header">
        <div className="header-brand">
          <div
            className="brand-icon"
            style={{
              background: "transparent",
              padding: 0,
              width: "auto",
              height: "auto",
            }}
          >
            <img
              src="/img/SGK_logo.png"
              alt="SGK Home Delivery"
              style={{ height: 40, width: "auto", display: "block" }}
            />
          </div>
          <div className="brand-name">
            Fleet<span>Ops</span>
          </div>
        </div>
        <div className="header-date-bar">
          <button className="date-nav-btn" onClick={prevDay}>
            &#8249;
          </button>
          <div className="date-center">
            <div className="date-day">{di.dayFull}</div>
            <div className="date-full">{di.dateStr}</div>
          </div>
          <button className="date-nav-btn" onClick={nextDay}>
            &#8250;
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="hv-tabs">
        <button
          className={`hv-tab ${activeTab === "builder" ? "active" : ""}`}
          onClick={() => setActiveTab("builder")}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="3"
              width="7"
              height="7"
              rx="1"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <rect
              x="14"
              y="3"
              width="7"
              height="7"
              rx="1"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <rect
              x="3"
              y="14"
              width="7"
              height="7"
              rx="1"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <rect
              x="14"
              y="14"
              width="7"
              height="7"
              rx="1"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </svg>
          Routes
        </button>
        <button
          className={`hv-tab ${activeTab === "profiles" ? "active" : ""}`}
          onClick={() => setActiveTab("profiles")}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path
              d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <circle
              cx="9"
              cy="7"
              r="4"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          Crew Profiles
        </button>
        <button
          className={`hv-tab ${activeTab === "dashboard" ? "active" : ""}`}
          onClick={() => setActiveTab("dashboard")}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="3"
              width="8"
              height="5"
              rx="1"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <rect
              x="13"
              y="3"
              width="8"
              height="11"
              rx="1"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <rect
              x="3"
              y="10"
              width="8"
              height="11"
              rx="1"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <rect
              x="13"
              y="16"
              width="8"
              height="5"
              rx="1"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </svg>
          Dashboard
        </button>
        <button
          className={`hv-tab ${activeTab === "dayoff" ? "active" : ""}`}
          onClick={() => setActiveTab("dayoff")}
          style={{ position: "relative" }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="4"
              width="18"
              height="18"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M16 2v4M8 2v4M3 10h18"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <path
              d="M8 14h4M8 18h6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          Day-Off Requests
          {dayoffPending > 0 && (
            <span
              style={{
                position: "absolute",
                top: 4,
                right: 4,
                background: "#E85520",
                color: "#000",
                borderRadius: "50%",
                width: 16,
                height: 16,
                fontSize: 9,
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
              }}
            >
              {dayoffPending > 9 ? "9+" : dayoffPending}
            </span>
          )}
        </button>
      </div>

      {/* ── ROUTES TAB (renamed from Route Builder) ── */}
      {activeTab === "builder" && <RouteBuilder />}

      {/* ── CREW PROFILES TAB ── */}
      {activeTab === "profiles" && (
        <div style={{ padding: "16px 0" }}>
          <ProfileSection />
        </div>
      )}

      {/* ── DAY-OFF REQUESTS TAB ── */}
      {activeTab === "dayoff" && <DayOffRequestTab />}

      {/* ── DASHBOARD TAB ── */}
      {activeTab === "dashboard" && <DashboardTab />}

      {/* ── NOTES TAB ── */}
      {activeTab === "notes" && (
        <div style={{ padding: 16, fontFamily: "'Barlow',sans-serif" }}>
          <div
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: 18,
              fontWeight: 800,
              textTransform: "uppercase",
              color: "#e8e9eb",
              marginBottom: 4,
            }}
          >
            Crew Notes
          </div>
          <div style={{ fontSize: 12, color: "#7a7d85", marginBottom: 16 }}>
            Notes left by drivers and sidemen when accepting their routes.
          </div>

          {allNotes.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 20px",
                color: "#454850",
              }}
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                style={{ margin: "0 auto 12px", display: "block" }}
              >
                <path
                  d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                  stroke="#454850"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p style={{ fontSize: 13 }}>
                No notes yet. Notes appear here when drivers or sidemen include
                a message when accepting their route.
              </p>
            </div>
          ) : (
            allNotes.map((n, i) => (
              <div
                key={i}
                style={{
                  background: "#141518",
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 12,
                  padding: "14px 16px",
                  marginBottom: 12,
                  borderLeft: `3px solid ${n.role === "driver" ? "#E85520" : "#3ecf6e"}`,
                }}
              >
                {/* Header row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    marginBottom: 10,
                    gap: 12,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    {/* Name + role badge + route */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                        marginBottom: 6,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "'Barlow Condensed',sans-serif",
                          fontSize: 17,
                          fontWeight: 800,
                          color: "#e8e9eb",
                        }}
                      >
                        {n.name}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: 10,
                          background:
                            n.role === "driver"
                              ? "rgba(232,85,32,0.15)"
                              : "rgba(62,207,110,0.15)",
                          border: `1px solid ${n.role === "driver" ? "rgba(232,85,32,0.3)" : "rgba(62,207,110,0.3)"}`,
                          color: n.role === "driver" ? "#E85520" : "#3ecf6e",
                          textTransform: "uppercase",
                          letterSpacing: "0.4px",
                        }}
                      >
                        {n.role}
                      </span>
                      <span
                        style={{
                          fontFamily: "'Barlow Condensed',sans-serif",
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#E85520",
                        }}
                      >
                        {n.routeNumber}
                      </span>
                    </div>
                    {/* Phone number prominently under the name */}
                    {n.phone ? (
                      <a
                        href={`tel:${n.phone}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          marginBottom: 5,
                          textDecoration: "none",
                        }}
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.12 6.12l.92-.92a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                            stroke="#E85520"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#E85520",
                          }}
                        >
                          {n.phone}
                        </span>
                      </a>
                    ) : (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#454850",
                          fontStyle: "italic",
                          marginBottom: 5,
                        }}
                      >
                        No phone on file
                      </div>
                    )}
                    {/* Date + time */}
                    <div style={{ fontSize: 11, color: "#7a7d85" }}>
                      {n.workDate} · {formatTime(n.confirmedAt)}
                    </div>
                  </div>
                  {/* Delete button */}
                  <button
                    onClick={() =>
                      deleteNote(n.crewId, n.role as "driver" | "sideman")
                    }
                    title="Delete note"
                    style={{
                      background: "none",
                      border: "1px solid rgba(224,82,82,0.25)",
                      borderRadius: 7,
                      color: "#e05252",
                      cursor: "pointer",
                      padding: "5px 10px",
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      flexShrink: 0,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "rgba(224,82,82,0.1)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "none")
                    }
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Delete
                  </button>
                </div>
                {/* Note text */}
                <div
                  style={{
                    background: "#1c1e22",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#e8e9eb",
                    lineHeight: 1.6,
                    borderLeft: "2px solid rgba(232,85,32,0.3)",
                  }}
                >
                  "{n.note}"
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
};

export default HomeView;
